import { useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SelectedValues } from '@/components/shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { TemplateTypeWithAllTab } from '@/types';

interface UsePoiFilterDeepLinkingProps {
  selectedFilterValues: SelectedValues;
  setSelectedFilterValues: React.Dispatch<React.SetStateAction<SelectedValues>>;
  customFilterValues: Record<string, string[]>;
  handleCustomFilterChange: (key: string, values: string[]) => void;
  workpackIds: string[];
  setWorkpackIds: React.Dispatch<React.SetStateAction<string[]>>;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  selectedPOIType: number;
  setSelectedPOIType: React.Dispatch<React.SetStateAction<number>>;
  allPOIsTemplatesWithAllTab: TemplateTypeWithAllTab[];
}

/**
 * Custom hook to handle deep linking for POI filters and tab selection.
 * Syncs filter state to URL parameters bidirectionally and restores state from URL on mount.
 */
export const usePoiFilterDeepLinking = ({
  selectedFilterValues,
  setSelectedFilterValues,
  customFilterValues,
  handleCustomFilterChange,
  workpackIds,
  setWorkpackIds,
  searchTerm,
  setSearchTerm,
  selectedPOIType,
  setSelectedPOIType,
  allPOIsTemplatesWithAllTab,
}: UsePoiFilterDeepLinkingProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isRestoringRef = useRef(true);
  const hasRestoredRef = useRef(false);

  // Restore filters from URL on mount
  useEffect(() => {
    // Skip if already restored
    if (hasRestoredRef.current) {
      return;
    }

    // Wait for templates to load (more than just "All" tab)
    if (allPOIsTemplatesWithAllTab.length <= 1) {
      return;
    }

    const poiFiltersParam = searchParams.get('poiFilters');
    const poiSearchTermParam = searchParams.get('poiSearchTerm');
    const poiWorkpacksParam = searchParams.get('poiWorkpacks');
    const poiTypeParam = searchParams.get('poiType');

    // Restore POI type/tab
    if (poiTypeParam) {
      try {
        // Try multiple matching strategies
        const tabIndex = allPOIsTemplatesWithAllTab.findIndex((template) => {
          const keyMatch = template.key?.toLowerCase() === poiTypeParam.toLowerCase();
          const nameMatch = template.name?.toLowerCase() === poiTypeParam.toLowerCase();
          const typeMatch = template.type?.toLowerCase() === poiTypeParam.toLowerCase();
          return keyMatch || nameMatch || typeMatch;
        });

        if (tabIndex !== -1 && tabIndex !== selectedPOIType) {
          setSelectedPOIType(tabIndex);
        }
      } catch (error) {
        console.error('[Deep Linking] Failed to restore POI type from URL:', error);
      }
    }

    // Restore filter values (static + template-specific + custom)
    if (poiFiltersParam) {
      try {
        const parsedFilters = JSON.parse(decodeURIComponent(poiFiltersParam));
        if (Object.keys(parsedFilters).length > 0) {
          // Extract special filters that have separate state
          const {
            deckRelativeHeight,
            workpackIds: customWorkpackIds,
            ...regularFilters
          } = parsedFilters;

          // Restore custom filters (deckRelativeHeight, workpackIds)
          if (deckRelativeHeight && Array.isArray(deckRelativeHeight)) {
            handleCustomFilterChange('deckRelativeHeight', deckRelativeHeight);
          }

          // Note: workpackIds in customFilters is the workpack filter dropdown
          // This is different from the workpackIds state used for filtering
          if (
            customWorkpackIds &&
            Array.isArray(customWorkpackIds) &&
            customWorkpackIds.length > 0
          ) {
            // Set to custom filter (for the dropdown UI)
            handleCustomFilterChange('workpackIds', customWorkpackIds);
            // Also set to the separate workpackIds state (for actual filtering)
            setWorkpackIds(customWorkpackIds);
          }

          // Set regular filter values (including selectedDeckIds)
          setSelectedFilterValues(regularFilters);
        }
      } catch (error) {
        console.error('[Deep Linking] Failed to parse poiFilters from URL:', error);
      }
    }

    // Restore search term
    if (poiSearchTermParam) {
      const decodedSearchTerm = decodeURIComponent(poiSearchTermParam);
      if (decodedSearchTerm !== searchTerm) {
        setSearchTerm(decodedSearchTerm);
      }
    }

    // Restore workpack IDs
    if (poiWorkpacksParam) {
      try {
        const parsedWorkpacks = JSON.parse(decodeURIComponent(poiWorkpacksParam));
        if (Array.isArray(parsedWorkpacks) && parsedWorkpacks.length > 0) {
          setWorkpackIds(parsedWorkpacks);
        }
      } catch (error) {
        console.error('[Deep Linking] Failed to parse poiWorkpacks from URL:', error);
      }
    }

    // Mark restoration as complete
    hasRestoredRef.current = true;

    // Small delay before enabling URL sync to ensure state updates have propagated
    setTimeout(() => {
      isRestoringRef.current = false;
    }, 100);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPOIsTemplatesWithAllTab.length]); // Re-run when templates load

  // Sync filters to URL whenever they change
  useEffect(() => {
    // Skip sync during initial restoration
    if (isRestoringRef.current) {
      return;
    }

    // Log existing URL params from searchParams
    const existingParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      existingParams[key] = value;
    });

    // Get ALL params from window.location to ensure we don't lose any
    const currentUrl = new URLSearchParams(window.location.search);
    const allExistingParams: Record<string, string> = {};
    currentUrl.forEach((value, key) => {
      allExistingParams[key] = value;
    });

    // Start with ALL existing params from window.location
    const newParams = new URLSearchParams(window.location.search);

    // Sync POI type/tab
    const currentTemplate = allPOIsTemplatesWithAllTab[selectedPOIType];
    if (currentTemplate && selectedPOIType !== 0) {
      // Don't add param for "All POIs" (index 0)
      newParams.set('poiType', currentTemplate.key || currentTemplate.name);
    } else {
      newParams.delete('poiType');
    }

    // Sync filter values (merge workpackIds and customFilterValues into selectedFilterValues for URL)
    const filtersToSync = { ...selectedFilterValues };

    // Add workpackIds to filters if present
    if (workpackIds.length > 0) {
      filtersToSync.workpackIds = workpackIds;
    }

    // Add custom filter values (deckRelativeHeight, workpackFilter)
    Object.keys(customFilterValues).forEach((key) => {
      if (customFilterValues[key].length > 0) {
        filtersToSync[key] = customFilterValues[key];
      }
    });

    if (Object.keys(filtersToSync).length > 0) {
      newParams.set('poiFilters', encodeURIComponent(JSON.stringify(filtersToSync)));
    } else {
      newParams.delete('poiFilters');
    }

    // Sync search term
    if (searchTerm && searchTerm.trim() !== '') {
      newParams.set('poiSearchTerm', encodeURIComponent(searchTerm));
    } else {
      newParams.delete('poiSearchTerm');
    }

    // Sync workpack IDs
    if (workpackIds.length > 0) {
      newParams.set('poiWorkpacks', encodeURIComponent(JSON.stringify(workpackIds)));
    } else {
      newParams.delete('poiWorkpacks');
    }

    // Log what params are in newParams
    const newParamsObj: Record<string, string> = {};
    newParams.forEach((value, key) => {
      newParamsObj[key] = value;
    });

    setSearchParams(newParams, { replace: true });
  }, [
    selectedFilterValues,
    customFilterValues,
    workpackIds,
    searchTerm,
    selectedPOIType,
    allPOIsTemplatesWithAllTab,
    searchParams,
    setSearchParams,
  ]);

  // Callback to manually sync filters to URL (useful for programmatic updates)
  const syncFiltersToUrl = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);

    const currentTemplate = allPOIsTemplatesWithAllTab[selectedPOIType];
    if (currentTemplate && selectedPOIType !== 0) {
      newParams.set('poiType', currentTemplate.key || currentTemplate.name);
    } else {
      newParams.delete('poiType');
    }

    if (Object.keys(selectedFilterValues).length > 0) {
      newParams.set('poiFilters', encodeURIComponent(JSON.stringify(selectedFilterValues)));
    } else {
      newParams.delete('poiFilters');
    }

    if (searchTerm && searchTerm.trim() !== '') {
      newParams.set('poiSearchTerm', encodeURIComponent(searchTerm));
    } else {
      newParams.delete('poiSearchTerm');
    }

    if (workpackIds.length > 0) {
      newParams.set('poiWorkpacks', encodeURIComponent(JSON.stringify(workpackIds)));
    } else {
      newParams.delete('poiWorkpacks');
    }

    setSearchParams(newParams, { replace: true });
  }, [
    selectedFilterValues,
    workpackIds,
    searchTerm,
    selectedPOIType,
    allPOIsTemplatesWithAllTab,
    searchParams,
    setSearchParams,
  ]);

  return { syncFiltersToUrl };
};
